import { useMemo } from 'react';
import { css } from '@emotion/react';
import { Feature } from '@weave/schema-gen-ts/dist/shared/feature/location_feature.pb';
import { CustomizationFlagQueries } from '@frontend/api-customization-flags';
import { useIsPAEligibleAccount, useShowCallIntelNav } from '@frontend/analytics';
import { useShowAiReceptionistAnalyticsNav } from '@frontend/analytics-ai-receptionist';
import { useHasCommunicationAnalyticsFeature } from '@frontend/analytics-communication';
import { useShowMarketingAttributionAnalyticsNav } from '@frontend/analytics-marketing-attribution';
import { useHasAutoRecallAnalyticsFeature } from '@frontend/analytics-shared';
import { useShowStaffPerformanceNav } from '@frontend/analytics-staff-performance';
import { AnimatedBadge } from '@frontend/components';
import { useTranslation } from '@frontend/i18n';
import { useMessagingFeaturesEnabled } from '@frontend/messaging-enabled-hook';
import { useAppScopeStore } from '@frontend/scope';
import { createNavItem, createSubNavItems } from '../nav-item-utils';
import { featureDefinition as analyticsFeatureDefinition } from './feature-definitions';

export const useAnalyticsNavItem = () => {
  const { t } = useTranslation('primaryNav');
  const hasCallIntelAccess = useShowCallIntelNav();
  const hasAutoRecallAnalyticsFeature = useHasAutoRecallAnalyticsFeature();
  const showAiReceptionistAnalyticsNav = useShowAiReceptionistAnalyticsNav();
  const showMarketingAttribution = useShowMarketingAttributionAnalyticsNav();
  const showStaffPerformanceNav = useShowStaffPerformanceNav();
  const messagingEnabled = useMessagingFeaturesEnabled();
  const [hasCommunicationAnalyticsFeature, isCommunicationAnalyticsFeatureLoading] =
    useHasCommunicationAnalyticsFeature();

  const { isPAEligibleAccount } = useIsPAEligibleAccount();
  const { selectedLocationIds } = useAppScopeStore();
  const { isFeatureHiddenInAllLocations: hideReviewsPage } =
    CustomizationFlagQueries.useAggregateCustomizationFlagDetails({
      locationIds: selectedLocationIds,
      enabled: true,
      customizationFlag: Feature.REVIEWS_HOME_ICON,
    });
  const analyticsItems = analyticsFeatureDefinition.items;

  const subNavItems = useMemo(
    () =>
      createSubNavItems([
        {
          ...analyticsItems['/dashboard'],
          relativePath: '/dashboard',
          label: t('Overview'),
        },
        {
          ...analyticsItems['/practice/main'],
          relativePath: '/practice/main',
          label: t('Practice'),
          show: isPAEligibleAccount,
        },
        {
          ...analyticsItems['/ai-receptionist/'],
          relativePath: '/ai-receptionist/',
          label: t('AI Receptionist'),
          show: showAiReceptionistAnalyticsNav,
        },
        {
          ...analyticsItems['/call-intelligence/main'],
          relativePath: '/call-intelligence/main',
          label: t('Call Intelligence'),
          show: hasCallIntelAccess,
        },
        {
          ...analyticsItems['/staff-performance'],
          relativePath: '/staff-performance',
          label: t('Staff Performance'),
          show: showStaffPerformanceNav,
        },
        {
          ...analyticsItems['/marketing-attribution/main'],
          relativePath: '/marketing-attribution/main',
          label: t('Marketing'),
          show: showMarketingAttribution,
        },
        {
          ...analyticsItems['/auto-recall'],
          relativePath: '/auto-recall',
          label: t('Auto Recall'),
          show: hasAutoRecallAnalyticsFeature,
        },
        {
          ...analyticsItems['/communication'],
          relativePath: '/communication',
          label: (
            <span css={{ display: 'flex', alignItems: 'center', gap: 'var(--fabric-space-gap-xs)' }}>
              {t('Communication')}
              <AnimatedBadge
                label={t('New')}
                styles={css`
                  height: var(--fabric-space-gap-md);
                  font-size: var(--fabric-typography-body-xs-font-size);
                  span {
                    padding: 0 var(--fabric-space-gap-xxs);
                  }
                `}
              />
            </span>
          ),
          show: hasCommunicationAnalyticsFeature && !isCommunicationAnalyticsFeatureLoading,
        },
        {
          ...analyticsItems['/phone/main'],
          relativePath: '/phone/main',
          label: t('Phone'),
        },
        {
          ...analyticsItems['/messaging'],
          relativePath: '/messaging',
          label: t('Messaging'),
          show: messagingEnabled,
        },
        {
          ...analyticsItems['/appointment'],
          relativePath: '/appointment',
          label: t('Appointment'),
        },
        {
          ...analyticsItems['/reviews'],
          relativePath: '/reviews',
          label: t('Reviews'),
          show: !hideReviewsPage,
        },
      ]),
    [
      t,
      isPAEligibleAccount,
      showStaffPerformanceNav,
      showAiReceptionistAnalyticsNav,
      hasCallIntelAccess,
      showMarketingAttribution,
      hasAutoRecallAnalyticsFeature,
      hideReviewsPage,
      messagingEnabled,
      analyticsItems,
      hasCommunicationAnalyticsFeature,
      isCommunicationAnalyticsFeatureLoading,
    ]
  );

  return useMemo(
    () =>
      createNavItem('analytics', {
        iconName: 'analytics',
        label: t('Analytics'),
        root: '/analytics',
        subNavItems,
      }),
    [t, subNavItems]
  );
};
